import KastAppClient, { Axios, KastAppClientForbiddenType, OAuth2DeviceVerifyDTO, RoomOutputDTO, TimeEventOutputDTO, TimeEventQueryMeetingPopulate, TimeEventQueryPopulate, TimeEventTargetModels, Tokens, UserOutputDTO } from '@kalyzee/kast-web-sdk/http';
import {
  ActionCreatorWithoutPayload,
  ActionCreatorWithPayload,
  Middleware,
  PayloadAction
} from '@reduxjs/toolkit';
import { ApiError } from '../../helpers/apiError';
import { getGlobalQueryLabel, NavigateGlobalQuery } from '../../helpers/navigate';
import { sdkClient } from '../../helpers/sdk';
import { getToken, setToken as setLocalStorageToken, TokenType } from '../../helpers/storage';
import { toastApiError, toastError } from '../../helpers/toast';
import { RequestLoginLocal, RequestOauth2AllowDevice } from '../../interfaces/request';
import { AuthStatus } from '../../interfaces/session';
import { setRoom } from '../room/slices';
import { login, logout, SessionMode, setAuthStatus, setRefreshToken, setToken, setUser } from '../session/slices';
import { AppMiddlewareApi } from '../store';
import requestActions from './actions';

/// Redux Action Handlers ///


interface ActionEntryWithPayload<P = any> {
  actionCreator: ActionCreatorWithPayload<P>,
  handle: (
    client: KastAppClient,
    store: AppMiddlewareApi,
    payload: P,
  ) => (Promise<any> | void),
}

interface ActionEntry {
  actionCreator: ActionCreatorWithoutPayload,
  handle: (client: KastAppClient, store: AppMiddlewareApi) => void,
}

const reduxActionHandlers: (ActionEntry | ActionEntryWithPayload)[] = [
  {
    actionCreator: requestActions.requestLoginLocal,
    handle: async (client: KastAppClient, store: AppMiddlewareApi, payload: RequestLoginLocal) => {
      let response: Tokens | undefined;
      try {
        response = await client.login(payload.email, payload.password);
        if (!response?.token) throw new Error('Token is undefined');
        store.dispatch(login({ token: response.token, refreshToken: response.refreshToken }))
      } catch(err: any) {
        if (ApiError.isApiError(err)) toastApiError(err);
        else toastError(`Error login : ${err?.message}`);
      }
      return response;
    },
  },
  {
    actionCreator: requestActions.requestLogout,
    handle: (client: KastAppClient, store: AppMiddlewareApi) => {
      store.dispatch(logout());
      return undefined;
    },
  },
  {
    actionCreator: requestActions.requestGetMe,
    handle: async (client: KastAppClient, store: AppMiddlewareApi) => {
      let response: Axios.AxiosResponse<UserOutputDTO, any> | undefined;
      try {
        response = await client.user.getMe();
        if (response) store.dispatch(setUser(response.data));
      } catch(err: any) {
        console.error(`Error get me : ${err?.message}`);
        throw err;
      }
      return response;
    },
  },
  {
    actionCreator: requestActions.requestSocketToken,
    handle: async (client: KastAppClient, store: AppMiddlewareApi) => {
      return await client.socket.getToken();
    },
  },
  {
    actionCreator: requestActions.requestOauth2AllowDevice,
    handle: async (client: KastAppClient, store: AppMiddlewareApi, payload: RequestOauth2AllowDevice) => {
      const data: OAuth2DeviceVerifyDTO = {
        user_code: payload.code,
        allow: true,
      };
      return await client.oAuth2.deviceVerify(data);
    },
  },
  {
    actionCreator: requestActions.requestGetRoom,
    handle: async (client: KastAppClient, store: AppMiddlewareApi, payload: { roomId: string }) => {
      let response: Axios.AxiosResponse<RoomOutputDTO, any> | undefined;
      try {
        response = await client.room.getById(payload.roomId);
        if (response) store.dispatch(setRoom(response.data));
        if (!response.data) throw new Error('No room with this id');
      } catch(err: any) {
        if (ApiError.isApiError(err)) toastApiError(err);
        throw err;
      }
      return response;
    },
  },
  {
    actionCreator: requestActions.requestGetTimeEvents,
    handle: async (client: KastAppClient, store: AppMiddlewareApi, payload: { room?: string, beforeDate?: Date, afterDate?: Date, populate?: TimeEventQueryPopulate[], meetingFilter?: {ids?: string[]} }) => {
      let response: Axios.AxiosResponse<TimeEventOutputDTO[], any> | undefined;
      try {
        response = await client.timeEvent.getAll({
          ...payload,
          meetingPopulate: [TimeEventQueryMeetingPopulate.ACTOR_IDENTITY],
          targets: [TimeEventTargetModels.Meeting]
        });
      } catch(err: any) {
        if (ApiError.isApiError(err)) toastApiError(err);
        throw err;
      }
      return response;
    },
  },
];

const reduxActionMatcher = (
  client: KastAppClient,
  store: AppMiddlewareApi,
  action: PayloadAction,
): Promise<any> | undefined => {
  // eslint-disable-next-line no-restricted-syntax
  for (const handler of reduxActionHandlers) {
    if (handler.actionCreator.match(action)) {
      const result = handler.handle(client, store, action.payload);
      if (result instanceof Promise) return result;
      return undefined;
    }
  }
  return undefined;
};

/// Middleware ///

export const requestMiddleware: Middleware = (store: AppMiddlewareApi) => {
  // eslint-disable-next-line arrow-body-style
  const tokens: Tokens = {
    token: getToken(TokenType.Normal) ?? undefined,
    refreshToken: getToken(TokenType.Refresh) ?? undefined,
  };
  if (tokens.token) {
    sdkClient.authenticateHttpClient({ method: { type: "tokens", data: tokens } }).then((result) => {
      if (result) store.dispatch(setAuthStatus(AuthStatus.In));
      else store.dispatch(setAuthStatus(AuthStatus.Out));
    }).catch((err) => {
      console.log("Error catched: ", err)
      store.dispatch(setAuthStatus(AuthStatus.Out));
    });
  }
  const httpClient = sdkClient.getHttpClient();
  httpClient.addEventListener('forbidden', (type: KastAppClientForbiddenType) => {
    const mode = store.getState().session.mode;
    store.dispatch(logout());
    let reason: string | undefined;
    if (type === KastAppClientForbiddenType.INSUFFICIENT_RIGHTS) reason = 'insufficientrights';
    if (type === KastAppClientForbiddenType.USER_IS_DISABLED) reason = 'udisabled';
    if (type === KastAppClientForbiddenType.USER_IS_BANNED) reason = 'ubanned';
    if (type === KastAppClientForbiddenType.USER_TRIAL_PERIOD_IS_OVER) reason = 'utrialperiodisover';
    if (type === KastAppClientForbiddenType.ORGANIZATION_IS_DISABLED) reason = 'odisabled';
    if (type === KastAppClientForbiddenType.ORGANIZATION_TRIAL_PERIOD_IS_OVER) reason = 'otrialperiodisover';
    if (reason) {
      window.location.href = `/forbidden?r=${reason}${mode === SessionMode.SLAVE ? `&${getGlobalQueryLabel(NavigateGlobalQuery.SLAVE)}=true` : ''}`;
    } else {
      window.location.href = `/login?${mode === SessionMode.SLAVE ? `${getGlobalQueryLabel(NavigateGlobalQuery.SLAVE)}=true` : ''}`;
    }
  });
  httpClient.addEventListener('tokenRefresh', (tokens) => {
    if (!tokens) return;
    const { token, refreshToken } = tokens;
    if (token) {
      setLocalStorageToken(token, TokenType.Normal);
      store.dispatch(setToken(token));
    }
    if (refreshToken) {
      setLocalStorageToken(refreshToken, TokenType.Refresh);
      store.dispatch(setRefreshToken(refreshToken));
    }
  });
  httpClient.addEventListener('tokenExpire', () => {
    const mode = store.getState().session.mode;
    const reason = 'sessionexpired';
    store.dispatch(logout());
    window.location.href = `/forbidden?r=${reason}${mode === SessionMode.SLAVE ? `&${getGlobalQueryLabel(NavigateGlobalQuery.SLAVE)}=true` : ''}`;
  });

  return (next) => async (action) => {
    const result = reduxActionMatcher(httpClient, store, action);
    if (result) return result;
    return next(action);
  };
};
